// The happens-before graph, laid out.
//
// Every trace event is a node — except a transaction's, which are drawn as one
// node and expand on a click, since nothing inside an attempt is separately
// observable, and an access to an address only one thread ever touched, which is
// not part of the concurrency story at all. Every immediate causal edge is drawn
// (spliced through the accesses that aren't). Threads
// give the colour, the causal order gives the vertical rank, and the cross-thread
// edges — a read reaching back to the write it observed, a child to its spawn —
// are the ones that matter: an edge is a dependency, its absence is concurrency,
// and that is the exact relation a partial-order reduction reasons over.
//
// Layout is Sugiyama's, via dagre — the bipartite hand-layout the txn graph uses
// isn't up to a real DAG (crossing minimisation, layered ranks). The model is
// pure and testable in ../hb-graph.ts; only the geometry lives here.

import { useEffect, useMemo, useState } from "react";
import dagre from "@dagrejs/dagre";
import type { RunResult } from "../../src/backend/interp/interpreter.js";
import { buildHBGraph } from "../hb-graph.js";
import type { HBEdgeKind, HBGraph, HBNode, HBTxn } from "../hb-graph.js";
import { confirmedRacesIn } from "../race-oracle.js";
import type { Reorder } from "../reorder.js";
import { describeKey } from "../trace-layout.js";
import type { ObjectTypes } from "../trace-layout.js";

interface HBGraphPanelProps {
  result: RunResult | null;
  /** The program and run parameters, so the panel can confirm which of the
   *  graph's accesses actually race (detect + decide, against this run). */
  source: string;
  seed: number;
  maxTime: number;
  strategy: "occ" | "hybrid";
  types?: ObjectTypes;
  /** The trace row selected elsewhere — drawn as the selected node. */
  selectedRow: number;
  /** Click a node to select its event across the playground. */
  onSelectRow(row: number): void;
  /** The race this run is currently flipped to, if any: the run on screen is the
   *  serialized ordering, and this is the drag that got there. Set → draw the
   *  toggle-back edge; null → the run is the default order, draw the race. */
  flippedRace?: Reorder | null;
  /** Flip the playground between the two orderings a race names. A race's own
   *  serialization flips to where the read observes the write; null flips back to
   *  the default order where it doesn't. */
  onFlipRace(serialization: Reorder | null): void;
}

/** A race, drawn: the read that went stale and the rival write concurrent with
 *  it, which the schedule left unordered. Not a happens-before edge — the very
 *  absence of one. */
interface RaceLink {
  read: number;
  rival: number;
  addr: string;
  /** The drag that serializes this pair — what a click on the edge applies to
   *  flip to the ordering where the read observes the write. */
  serialization: Reorder;
}

/** The realized reads-from a flip lands on: the same two nodes, now ordered.
 *  Located by the flip's own step ids in the *serialized* run on screen, so it
 *  survives the re-run the flip caused. */
interface FlipLink {
  read: number;
  write: number;
}

/** Above this many nodes the oracle pass (a couple of runs per candidate) isn't
 *  worth the stall, and a graph that big isn't one you study race-by-race
 *  anyway. Race marks are for the small programs the panel is really for. */
const RACE_NODE_CAP = 150;

/** Thread → colour, from the op palette, so a thread reads the same here as in
 *  the trace's lifelines. */
const THREAD_VARS = ["--op-thread", "--op-txn", "--op-sleep", "--op-spawn", "--op-commit", "--op-lock"];

/** Edge colour by kind. `program` is the quiet spine; `reads-from` is lit,
 *  because it is the data-flow edge a race lives on. */
const EDGE_STYLE: Record<HBEdgeKind, { stroke: string; width: number; dash?: string }> = {
  program: { stroke: "var(--border)", width: 1.5 },
  "reads-from": { stroke: "var(--op-lock)", width: 1.75 },
  fork: { stroke: "var(--op-spawn)", width: 1.5, dash: "4 3" },
  wake: { stroke: "var(--op-commit)", width: 1.5, dash: "4 3" },
  sync: { stroke: "var(--op-wait)", width: 1.5, dash: "2 3" },
  start: { stroke: "var(--muted)", width: 1.5 },
};

// `start` isn't in the legend — the labelled "program start" node speaks for it.
const EDGE_ORDER: HBEdgeKind[] = ["program", "reads-from", "fork", "wake", "sync"];
const EDGE_LABEL: Record<HBEdgeKind, string> = {
  program: "program order",
  "reads-from": "reads-from",
  fork: "fork",
  wake: "retry wake",
  sync: "lock / validate",
  start: "program start",
};

const NODE_H = 24;
const CHAR_W = 6.4;
const NODE_PAD = 14;

interface Placed {
  node: HBNode;
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
}
interface RoutedEdge {
  kind: HBEdgeKind;
  points: { x: number; y: number }[];
}
/** An opened transaction, as dagre placed it: the box around its own events. */
interface PlacedGroup {
  txn: HBTxn;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Layout {
  placed: Placed[];
  edges: RoutedEdge[];
  groups: PlacedGroup[];
  width: number;
  height: number;
}

/** Cluster ids live in the same namespace as node ids (dagre keys by string), so
 *  they are prefixed — and filtered back out of the placed nodes. */
const CLUSTER = "txn:";
const clusterId = (id: number): string => `${CLUSTER}${id}`;

function nodeLabel(n: HBNode): string {
  return n.detail ? `${n.op} ${n.detail}` : n.op;
}

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}
interface Pt {
  x: number;
  y: number;
}

const lerp = (a: Pt, b: Pt, t: number): Pt => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
const quadAt = (p0: Pt, p1: Pt, p2: Pt, t: number): Pt => lerp(lerp(p0, p1, t), lerp(p1, p2, t), t);
const inBox = (p: Pt, b: Box): boolean =>
  p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h;

/**
 * The visible part of the race link: a quadratic arc whose *shape* is fixed by
 * the two node centres — control point bowed off the centre-to-centre line — but
 * whose ends are trimmed to where the curve leaves each node's box, so the
 * portion buried under the boxes is never drawn. Trimming (rather than arcing
 * between the borders) keeps the curvature the same however deep the overlap is,
 * and lands the flip arrow's head right on the border it points at.
 *
 * The bow grows with the span but is capped, so short links curve gently and
 * long ones don't balloon.
 */
function arcBetween(fromBox: Box, toBox: Box): string | null {
  const p0: Pt = { x: fromBox.x + fromBox.w / 2, y: fromBox.y + fromBox.h / 2 };
  const p2: Pt = { x: toBox.x + toBox.w / 2, y: toBox.y + toBox.h / 2 };
  const dx = p2.x - p0.x;
  const dy = p2.y - p0.y;
  const len = Math.hypot(dx, dy) || 1;
  const bow = Math.min(34, Math.max(16, len * 0.22));
  const p1: Pt = { x: (p0.x + p2.x) / 2 + (-dy / len) * bow, y: (p0.y + p2.y) / 2 + (dx / len) * bow };

  // The t where the curve exits `fromBox`, and where it enters `toBox`. Sampled
  // finely — a couple of px of slack on a hidden overlap boundary is invisible.
  const N = 240;
  let t1 = 0;
  let t2 = 1;
  for (let i = 0; i <= N; i++) {
    if (!inBox(quadAt(p0, p1, p2, i / N), fromBox)) { t1 = i / N; break; }
  }
  for (let i = N; i >= 0; i--) {
    if (!inBox(quadAt(p0, p1, p2, i / N), toBox)) { t2 = i / N; break; }
  }
  if (t2 <= t1) return null; // one box swallows the other — nothing to draw

  // The sub-curve over [t1, t2], via De Casteljau: split at t1 and keep the
  // right piece, then split that at the remapped t2 and keep the left piece.
  const r0 = quadAt(p0, p1, p2, t1);
  const r1 = lerp(p1, p2, t1);
  const u = (t2 - t1) / (1 - t1);
  const s0 = r0;
  const s1 = lerp(r0, r1, u);
  const s2 = quadAt(p0, p1, p2, t2);
  return `M ${s0.x} ${s0.y} Q ${s1.x} ${s1.y} ${s2.x} ${s2.y}`;
}

function layoutGraph(graph: HBGraph): Layout {
  // Compound, so an *opened* transaction is a real cluster: dagre keeps its
  // events together and hands back the box around them. Drawing a bounding box
  // over a plain layout instead put half the graph inside the transaction.
  const g = new dagre.graphlib.Graph({ multigraph: true, compound: true });
  g.setGraph({ rankdir: "TB", ranksep: 30, nodesep: 16, marginx: 12, marginy: 12 });
  g.setDefaultEdgeLabel(() => ({}));

  const opened = graph.txns.filter((t) => !t.collapsed);
  for (const t of opened) g.setNode(clusterId(t.id), { label: `txn#${t.id}` });

  const label = new Map<number, string>();
  for (const n of graph.nodes) {
    const l = nodeLabel(n);
    label.set(n.index, l);
    // A collapsed attempt needs a gutter for its `+`, or the glyph sits on the
    // last letter of its label.
    const gutter = n.txn ? 14 : 0;
    g.setNode(String(n.index), { width: Math.max(40, l.length * CHAR_W + NODE_PAD + gutter), height: NODE_H });
  }
  for (const t of opened) {
    for (const row of t.rows) if (g.hasNode(String(row))) g.setParent(String(row), clusterId(t.id));
  }
  for (const [i, e] of graph.edges.entries()) {
    g.setEdge(String(e.from), String(e.to), {}, String(i));
  }
  dagre.layout(g);

  const byIndex = new Map(graph.nodes.map((n) => [n.index, n]));
  const placed: Placed[] = g.nodes().filter((id) => !id.startsWith(CLUSTER)).map((id) => {
    const nd = g.node(id);
    const index = Number(id);
    return {
      node: byIndex.get(index)!,
      x: nd.x - nd.width / 2,
      y: nd.y - nd.height / 2,
      w: nd.width,
      h: nd.height,
      label: label.get(index) ?? "",
    };
  });
  // Ascending by trace row, so a node keeps its position in the DOM across a
  // relayout. Dagre's own order is insertion order and shifts as attempts open
  // and close; React would then re-insert the elements, and a re-inserted
  // element replays its entry animation — every surviving node flashing on every
  // expand.
  placed.sort((a, b) => a.node.index - b.node.index);
  const edges: RoutedEdge[] = graph.edges.map((e, i) => ({
    kind: e.kind,
    points: g.edge({ v: String(e.from), w: String(e.to), name: String(i) }).points,
  }));
  const groups: PlacedGroup[] = opened.flatMap((txn) => {
    const nd = g.node(clusterId(txn.id));
    if (!nd) return [];
    return [{ txn, x: nd.x - nd.width / 2, y: nd.y - nd.height / 2, w: nd.width, h: nd.height }];
  });
  const { width = 0, height = 0 } = g.graph();
  return { placed, edges, groups, width, height };
}

export function HBGraphPanel({
  result,
  source,
  seed,
  maxTime,
  strategy,
  types,
  selectedRow,
  onSelectRow,
  flippedRace,
  onFlipRace,
}: HBGraphPanelProps) {
  // Which transaction attempts are drawn open. Collapsed is the default — an
  // attempt is one thing the language does — and this is per run: an id means
  // something else in the next one.
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  // Bumped once per run, and mixed into the layer keys below. Within a run a
  // node keeps its element across relayouts so it can travel to its new place;
  // a *new* run is a different graph that merely reuses row numbers, and sliding
  // those into position would animate a correspondence that isn't there.
  const [run, setRun] = useState(0);
  useEffect(() => {
    setExpanded(new Set());
    setRun((n) => n + 1);
  }, [result]);
  const toggle = (id: number): void =>
    setExpanded((was) => {
      const next = new Set(was);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const graph = useMemo(() => (result ? buildHBGraph(result, types, expanded) : null), [result, types, expanded]);
  const layout = useMemo(() => (graph && graph.nodes.length ? layoutGraph(graph) : null), [graph]);
  // Which attempts are open, as a string — the identity of a layout. The edge
  // and race layers are keyed by it so they remount, and so replay their
  // fade-in, every time the geometry moves under them.
  const expandKey = useMemo(() => [...expanded].sort((a, b) => a - b).join(","), [expanded]);

  // The races confirmed *in this run* — the read that went stale, and the rival
  // write it raced with (the intruder of the A-B-A that the oracle confirmed
  // changes the outcome). A race is the *absence* of an ordering edge, so it is
  // drawn as its own kind of link, not looked for among the causal ones.
  const races: RaceLink[] = useMemo(() => {
    if (!result?.causality || !graph || graph.nodes.length > RACE_NODE_CAP) return [];
    return confirmedRacesIn(source, { seed, maxTime, strategy }, result).map((r) => ({
      read: r.candidate.first.row,
      rival: r.candidate.middle.row,
      addr: describeKey(r.candidate.addr, types),
      serialization: r.serialization,
    }));
  }, [result, graph, source, seed, maxTime, strategy, types]);

  // When the run on screen is a race flipped to its serialized ordering, the race
  // itself is gone from the graph — the read now reads-from the write, an
  // ordinary causal edge. What remains is the toggle: find those same two nodes
  // in *this* run by the flip's own step ids (stable across the re-run) so we can
  // draw the edge back to the default order. The flip's `target` is the rival
  // write's step, its `moved` a victim read's — the write it now observes.
  const flip: FlipLink | null = useMemo(() => {
    if (!flippedRace || !result?.causality || !graph) return null;
    const c = result.causality;
    const rowIn = (id: string, type: "read" | "write"): number => {
      const step = c.steps.findIndex((s) => s.id === id);
      if (step < 0) return -1;
      const s = c.steps[step]!;
      for (let i = s.firstEvent; i < s.endEvent; i++) {
        if (result.trace.events[i]!.type === type) return i;
      }
      return s.endEvent > s.firstEvent ? s.firstEvent : -1;
    };
    const read = rowIn(flippedRace.moved, "read");
    const write = rowIn(flippedRace.target, "write");
    return read >= 0 && write >= 0 ? { read, write } : null;
  }, [flippedRace, result, graph]);

  // The assertion verdict of the run on screen, which colours the race/flip edge:
  // red when some assertion failed, green when they all passed. `none` when the
  // program asserts nothing — then the edge keeps its structural colour (the race
  // is red because it is unordered, the flip green because it is committed to).
  // Colour is the *outcome*; the edge's dashed-vs-arrow shape is the *ordering*,
  // so clicking always flips the shape even on the runs where the verdict — and
  // so the colour — stays put (both orderings fail, both pass, or no assertion).
  const verdict: "pass" | "fail" | "none" = useMemo(() => {
    let saw = false;
    for (const ev of result?.trace.events ?? []) {
      if (ev.type === "assert") {
        saw = true;
        if (!ev.passed) return "fail";
      }
    }
    return saw ? "pass" : "none";
  }, [result]);
  const verdictColor = verdict === "fail" ? "var(--op-abort)" : verdict === "pass" ? "var(--op-commit)" : null;

  /** The fill a collapsed transaction wears: its thread's hue, at a depth that
   *  carries text. Not the lane colour itself — those are mid-tones, and a label
   *  on them tops out around 5:1 whichever ink it takes. See `--chip-*`. */
  const chipColor = (thread: number): string => {
    const lane = graph ? Math.max(0, graph.threads.indexOf(thread)) : 0;
    return `var(${THREAD_VARS[lane % THREAD_VARS.length]!.replace("--op-", "--chip-")})`;
  };

  const laneColor = (thread: number): string => {
    if (thread < 0) return "var(--muted)"; // the synthetic "program start" node
    const lane = graph ? graph.threads.indexOf(thread) : 0;
    return `var(${THREAD_VARS[lane % THREAD_VARS.length]})`;
  };

  if (!result) return <div id="hbgraph"><div className="hb-hint">run a program to see its happens-before graph</div></div>;
  if (!layout) return <div id="hbgraph"><div className="hb-hint">nothing to show</div></div>;

  // Node centres, for the race overlay — drawn between placed nodes rather than
  // handed to dagre, since a race link is bidirectional and non-causal and would
  // wreck the layered ranking.
  const box = new Map<number, Box>(layout.placed.map((p) => [p.node.index, { x: p.x, y: p.y, w: p.w, h: p.h }]));
  // A race/flip link is the centre-to-centre arc with its ends trimmed to the
  // node borders, so the shape is stable but the overlap under the boxes is
  // hidden. Null when a box is missing or one node engulfs the other.
  const link = (from: number, to: number): string | null => {
    const bf = box.get(from);
    const bt = box.get(to);
    return bf && bt ? arcBetween(bf, bt) : null;
  };

  // An opened attempt keeps its identity: dagre's own cluster box behind its
  // events, labelled, with the way back on it.
  const opened = layout.groups;

  return (
    <div id="hbgraph">
      <div className="hb-legend">
        {opened.length > 0 && (
          <button className="hb-collapse" onClick={() => setExpanded(new Set())}>
            collapse {opened.length === 1 ? "the open transaction" : `all ${opened.length} transactions`}
          </button>
        )}
        {graph!.threads.map((t) => (
          <span key={t} className="hb-leg-thread" style={{ color: laneColor(t) }}>
            ● T{t}
          </span>
        ))}
        <span className="hb-leg-sep" />
        {EDGE_ORDER.map((k) => (
          <span key={k} className="hb-leg-edge">
            <svg width="18" height="8" aria-hidden>
              <line
                x1="1" y1="4" x2="17" y2="4"
                stroke={EDGE_STYLE[k].stroke}
                strokeWidth={EDGE_STYLE[k].width}
                strokeDasharray={EDGE_STYLE[k].dash}
              />
            </svg>
            {EDGE_LABEL[k]}
          </span>
        ))}
        {graph!.elided.length > 0 && (
          <span
            className="hb-leg-elided"
            title="accesses to addresses only one thread ever touched — a library's own bookkeeping, an accumulator, a buffer. None of it can be part of a race or change another thread's view, so it is not part of this order and is not drawn; the program-order edges splice through it. Nothing is hidden from the trace, the debugger, or the step count."
          >
            {graph!.elided.length} thread-local {graph!.elided.length === 1 ? "access" : "accesses"} not
            drawn
          </span>
        )}
        {races.length > 0 && (
          <span className="hb-leg-edge hb-leg-race" title="two conflicting accesses left unordered, whose order changes the outcome. Colour is the assertion verdict of this ordering (red fails, green passes); dashed means unordered. Click the edge to flip to the ordering where the read observes the write.">
            <svg width="18" height="8" aria-hidden>
              <line x1="1" y1="4" x2="17" y2="4" stroke={verdictColor ?? "var(--op-abort)"} strokeWidth="2" strokeDasharray="5 3" />
            </svg>
            race — click to flip
          </span>
        )}
        {flip && (
          <span className="hb-leg-edge hb-leg-flip" title="flipped: the read now observes the write — the real reads-from edge, lit with an aura in the assertion verdict's colour (green passes, red fails). Click that edge to flip back to the default order.">
            <svg width="18" height="10" aria-hidden style={{ overflow: "visible" }}>
              <line x1="1" y1="5" x2="17" y2="5" stroke={verdictColor ?? "var(--op-commit)"} strokeWidth="5" opacity="0.4" strokeLinecap="round" />
              <line x1="1" y1="5" x2="17" y2="5" stroke={EDGE_STYLE["reads-from"].stroke} strokeWidth={EDGE_STYLE["reads-from"].width} />
            </svg>
            flipped — click to restore
          </span>
        )}
      </div>
      <div className="hb-scroll">
        <svg width={layout.width} height={layout.height} className="hb-svg">
          <defs>
            {EDGE_ORDER.map((k) => (
              <marker
                key={k}
                id={`hb-arrow-${k}`}
                viewBox="0 0 8 8"
                refX="7"
                refY="4"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M0,0 L8,4 L0,8 z" fill={EDGE_STYLE[k].stroke} />
              </marker>
            ))}
          </defs>
          <g key={`groups-${run}`}>
            {opened.map((g) => (
              // Placed by transform rather than by the rect's own x/y, so the box
              // and its label travel together when a relayout moves them: a
              // `<text>`'s x/y are plain attributes and would snap while the rect
              // slid out from under them.
              <g
                key={`txn-${g.txn.id}`}
                className="hb-group"
                transform={`translate(${g.x},${g.y})`}
                onClick={() => toggle(g.txn.id)}
              >
                <rect width={g.w} height={g.h} rx={6} />
                <text x={6} y={-3}>
                  txn#{g.txn.id} {g.txn.ended} −
                </text>
                <title>one transaction attempt, opened. Click to collapse it back into one node.</title>
              </g>
            ))}
          </g>
          {/* The causal edges, and below them the race arcs, are keyed by which
              attempts are open: a relayout remounts them, replaying the fade
              that keeps them out of the way while the nodes travel. A polyline's
              `points` can't be interpolated, and a routed edge has no honest
              in-between anyway — better absent than snapping mid-flight. */}
          <g key={`edges-${run}:${expandKey}`} className="hb-settle">
            {layout.edges.map((e, i) => {
              // In the flipped run the read genuinely reads-from the write, so that
              // edge is already in the DAG. Rather than draw a second link over it,
              // light *this* edge — an aura in the verdict colour — and make it the
              // clickable toggle back. Every edge on the graph stays a real edge.
              const ge = graph!.edges[i]!;
              const isFlip = !!flip && ge.kind === "reads-from" && ge.from === flip.write && ge.to === flip.read;
              const pts = e.points.map((p) => `${p.x},${p.y}`).join(" ");
              const edge = (
                <polyline
                  key={i}
                  points={pts}
                  fill="none"
                  stroke={EDGE_STYLE[e.kind].stroke}
                  strokeWidth={EDGE_STYLE[e.kind].width}
                  strokeDasharray={EDGE_STYLE[e.kind].dash}
                  markerEnd={`url(#hb-arrow-${e.kind})`}
                >
                  {isFlip && (
                    <title>
                      flipped: this read now observes the write —{" "}
                      {verdict === "fail"
                        ? "this ordering still fails its assertion"
                        : verdict === "pass"
                          ? "this ordering passes its assertion"
                          : "the reads-from is now realized"}
                      . Click to flip back to the default order, where it misses the write.
                    </title>
                  )}
                </polyline>
              );
              if (!isFlip) return edge;
              return (
                <g key={i} className="hb-race-toggle" onClick={() => onFlipRace(null)}>
                  <polyline
                    className="hb-flip-aura"
                    points={pts}
                    fill="none"
                    stroke={verdictColor ?? "var(--op-commit)"}
                  />
                  <polyline className="hb-race-hit" points={pts} fill="none" />
                  {edge}
                </g>
              );
            })}
          </g>
          {/* Race overlay: the order the graph refused to commit to. Bidirectional
              (arrowless) because neither access happens-before the other. The edge
              is the toggle — clicking it flips the run to the ordering where the
              read observes the write. A fat transparent line under it widens the
              hit target past the 2px stroke. */}
          <g key={`races-${run}:${expandKey}`} className="hb-settle">
            {races.map((r, i) => {
              const d = link(r.read, r.rival);
              if (!d) return null;
              return (
                <g key={`race-${i}`} className="hb-race-toggle" onClick={() => onFlipRace(r.serialization)}>
                  <path className="hb-race-hit" d={d} fill="none" />
                  <path
                    className="hb-race"
                    d={d}
                    fill="none"
                    stroke={verdictColor ?? "var(--op-abort)"}
                    strokeWidth="2"
                    strokeDasharray="5 3"
                    strokeLinecap="round"
                  >
                    <title>
                      race on {r.addr}: unordered, and ordering it changes the outcome —{" "}
                      {verdict === "fail"
                        ? "this ordering fails its assertion"
                        : verdict === "pass"
                          ? "this ordering passes its assertion"
                          : "this ordering leaves the read missing the write"}
                      . Click to flip to the ordering where the read observes the write.
                    </title>
                  </path>
                </g>
              );
            })}
          </g>
          <g key={`nodes-${run}`}>
            {layout.placed.map((p) => {
              const synthetic = p.node.index < 0; // the "program start" source
              const txn = p.node.txn;
              // A collapsed attempt is selected when *any* of its rows is: the node
              // stands for all of them, so the graph↔trace bridge has to as well.
              const sel = txn
                ? txn.rows.includes(selectedRow)
                : !synthetic && p.node.index === selectedRow;
              return (
                <g
                  key={p.node.index}
                  className={`hb-node${sel ? " sel" : ""}${synthetic ? " start" : ""}${txn ? " txn" : ""}`}
                  transform={`translate(${p.x},${p.y})`}
                  onClick={synthetic ? undefined : () => onSelectRow(p.node.index)}
                >
                  {/* A collapsed attempt is filled solid in its thread's colour,
                      with the label knocked out of it. It is a different *kind* of
                      thing from an event — one node standing for several — and
                      that reads at a glance, where an outline variation did not. */}
                  <rect
                    width={p.w}
                    height={p.h}
                    rx={synthetic ? p.h / 2 : 4}
                    fill={txn ? chipColor(p.node.thread) : "var(--panel)"}
                    stroke={txn ? chipColor(p.node.thread) : laneColor(p.node.thread)}
                    strokeWidth={sel ? 2.5 : 1.25}
                    strokeDasharray={synthetic ? "3 2" : undefined}
                  />
                  <text
                    x={p.w / 2}
                    y={p.h / 2}
                    className={txn ? "hb-label" : `hb-label ${p.node.opCls}`}
                  >
                    {p.label}
                  </text>
                  {txn && (
                    <g
                      className="hb-expand"
                      onClick={(e) => {
                        e.stopPropagation(); // expanding is not selecting
                        toggle(txn.id);
                      }}
                    >
                      <rect x={p.w - 15} y={0} width={15} height={p.h} fill="transparent" />
                      <text x={p.w - 7.5} y={p.h / 2}>
                        +
                      </text>
                      <title>
                        {txn.rows.length} events — begin, its accesses, and the {txn.ended}. Click to open
                        it; nothing inside is separately observable, which is why it is one node.
                      </title>
                    </g>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    </div>
  );
}
